import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonEditCard } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { Card, CardColorType, GoogleClassroomPostType } from "@shared/models/card-stack"
import Patch from "@shared/models/patch-updater"
import type { Narrative } from "../create-narrative"
import defaultLessonActivity from "./default-lesson-activity"
import defaultLessonContext from "./default-lesson-context"
import defaultLessonModels from "./default-lesson-models"

interface Payload {
  id: string
  type: Card["type"]
  color: CardColorType
  postType?: GoogleClassroomPostType
  cardStackId: string
  position: number
}

export const LESSON_CARD_ADD = {
  name: "LESSON_CARD_ADD",

  params: {
    id: "string",
    cardStackId: "string",
    position: "number",
  },

  patches(payload: Payload) {
    let card: Card

    if (payload.type === "card-standards") {
      card = {
        id: payload.id,
        type: "card-standards",
        attributes: {
          title: "Standards",
          standards: [],
          commentIds: [],
          comments: [],
          attachments: [],
          position: payload.position,
          color: payload.color,
          isPublic: false,
          parentCardId: null,
        },
      }
    } else if (payload.type === "card-google-classroom") {
      if (payload.postType === undefined) throw Error("Undefined post type")
      let extraPostProperties =
        payload.postType === "MULTIPLE_CHOICE_QUESTION" ? { choices: ["Option 1"] } : {}
      card = {
        id: payload.id,
        type: "card-google-classroom",
        attributes: {
          title: "",
          value: "",
          maxPoints: null,
          position: payload.position,
          color: payload.color,
          commentIds: [],
          comments: [],
          attachments: [],
          postType: payload.postType,
          attachmentSettings: {},
          isPublic: false,
          parentCardId: null,
          classSettings: [],
          enabledGoogleClassIds: [],
          _revision: 0,
          extraPostProperties: extraPostProperties,
        },
      }
    } else if (payload.type === "card-title-value-html") {
      card = {
        id: payload.id,
        type: "card-title-value-html",
        attributes: {
          title: "",
          value: "",
          position: payload.position,
          color: payload.color,
          isPublic: false,
          parentCardId: null,
          attachments: [],
          commentIds: [],
          comments: [],
        },
      }
    } else if (payload.type === "card-value-html") {
      card = {
        id: payload.id,
        type: "card-value-html",
        attributes: {
          value: "",
          position: payload.position,
          color: payload.color,
          isPublic: false,
          parentCardId: null,
          attachments: [],
          commentIds: [],
          comments: [],
        },
      }
    } else {
      throw new Error(`Incorrect Card Type. Tried to created ${payload.type}`)
    }

    let patch = Patch.create("card-stack", payload.cardStackId).push("attributes.cards", card).patch
    return [patch]
  },

  undoPatches(payload: Payload) {
    let patch = Patch.create("card-stack", payload.cardStackId).pull("attributes.cards", {
      id: payload.id,
    }).patch

    return [patch]
  },

  narrative(payload: Payload, findFn: FindFn, userId: string): Narrative {
    let models = defaultLessonModels(payload, findFn, userId)
    let context = defaultLessonContext(payload, models)
    context.cardType = payload.type
    let newEvent: LessonEditCard = {
      ...createBlankEvent(),
      root_id: models.planbook?.id ?? null,
      root_type: "planbook",
      date_plan_scheduled_for: models.dateStringForCardStack,
      primary_object_id: payload.cardStackId,
      primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
      primary_verb: "edit",
      primary_possessive: getPossessiveForPlanbook(findFn, userId, models.planbook?.id),
      primary_object_type: "lesson",
      primary_object_specifier: "cards",
      adverbial_verb: "adding",
      adverbial_object_type: "card",
      adverbial_object_specifier: null,
      adverbial_object_id: payload.id,
      adverbial_possessive: null,
      adverbial_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
    }

    return {
      title: "Added Card to Lesson",
      titleForAnalytics: "Lesson Card - Add",
      description: `Added card to "${context.cardStackTitle}" on "${context.date}"`,
      context: context,
      activity: defaultLessonActivity(payload, models, "created"),
      newAnalyticsEvents: [newEvent],
    }
  },
}

export default LESSON_CARD_ADD
